import { CommonModule, isPlatformBrowser } from '@angular/common';
import type { ElementRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  inject,
  Input,
  LOCALE_ID,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
} from '@angular/core';
import {
  ChallengeStatusTagComponent,
  ChallengeStatusTagType,
} from '@freelancer/components/challenge-status-tag';
import { PipesModule } from '@freelancer/pipes';
import { TrackingModule } from '@freelancer/tracking';
import { UiModule } from '@freelancer/ui';
import { HrColor } from '@freelancer/ui/hr';
import { flGroupOutline, IconSize, provideFlIcons } from '@freelancer/ui/icon';
import { LinkUnderline, QueryParamsHandling } from '@freelancer/ui/link';
import type { PictureBackgroundImageStyles } from '@freelancer/ui/picture';
import { PictureBgImageObjectFit, PictureLayout } from '@freelancer/ui/picture';
import { TagColor, TagSize } from '@freelancer/ui/tag';
import { TooltipModule } from '@freelancer/ui/tooltip';
import { DEFAULT_CARD_COVER_IMAGE_SIZE } from '../challenge-card.types';

const AVERAGE_CHAR_WIDTH = 7;
const TAG_CHROME_WIDTH = 18;
const PLUS_TAG_WIDTH = 40;
const TAG_GAP = 8;

@Component({
  providers: [provideFlIcons({ flGroupOutline })],
  selector: 'fl-challenge-card-default',
  standalone: true,
  imports: [
    CommonModule,
    ChallengeStatusTagComponent,
    TooltipModule,
    PipesModule,
    TrackingModule,
    UiModule,
  ],
  template: `
    @if (link) {
      <fl-link
        class="LinkWrapper"
        [display]="'flex'"
        [flexDirection]="'column'"
        [flTrackingLabel]="trackingLabel"
        [flTrackingReferenceId]="challengeId"
        [flTrackingReferenceType]="'challenge_id'"
        [link]="link"
        [queryParamsHandling]="QueryParamsHandling.PRESERVE"
        [underline]="LinkUnderline.NEVER"
      >
        <ng-container *ngTemplateOutlet="cardContent"></ng-container>
      </fl-link>
    } @else {
      <ng-container *ngTemplateOutlet="cardContent"></ng-container>
    }

    <ng-template #cardContent>
      <div class="Cover">
        @if (imageUrl) {
          <fl-picture
            class="Cover-image"
            [backgroundStyles]="backgroundStyles"
            [height]="CardCoverImageSize.height"
            [isBackgroundImage]="true"
            [layout]="PictureLayout.FIXED"
            [resizeFit]="'crop'"
            [src]="imageUrl"
            [width]="CardCoverImageSize.width"
          ></fl-picture>
        }
        <fl-challenge-status-tag
          class="Cover-statusTag"
          [status]="status"
        ></fl-challenge-status-tag>
        <h2
          class="Cover-title uppercase"
          [flMaxLines]="2"
        >
          {{ challengeTitle }}
        </h2>
      </div>
      <div class="Body">
        <div class="Meta">
          @if (skills.length > 0) {
            <div
              #skillsRow
              class="Meta-skills"
            >
              @for (skill of displayedSkills; track skill) {
                <fl-tag
                  class="Meta-skills-tag"
                  [color]="TagColor.INHERIT"
                  [size]="TagSize.SMALL"
                >
                  {{ skill }}
                </fl-tag>
              }
              @if (hiddenSkillCount > 0) {
                <fl-tag
                  class="Meta-skills-tag"
                  tabindex="0"
                  [color]="TagColor.INHERIT"
                  [matTooltip]="hiddenSkillsTooltip"
                  [size]="TagSize.SMALL"
                >
                  +{{ hiddenSkillCount }}
                </fl-tag>
              }
            </div>
          }
          @if (showEntryCount) {
            <span class="Meta-entries">
              <fl-icon
                [name]="'flGroupOutline'"
                [size]="IconSize.MID"
              ></fl-icon>
              <p>
                {{ entryCount }}
                {{ entryCount | i18nPlural: entryCountPluralMapping }}
              </p>
            </span>
          } @else if (startDateHasPassed) {
            <p
              class="Meta-startDate"
              i18n="Challenge card started date"
            >
              Started {{ startDate | date: 'mediumDate' }}
            </p>
          } @else {
            <p
              class="Meta-startDate"
              i18n="Challenge card start date"
            >
              Starts {{ startDate | date: 'mediumDate' }}
            </p>
          }
        </div>

        @if (description) {
          <p
            class="Description text-body-16"
            [flMaxLines]="3"
          >
            {{ description }}
          </p>
        }

        <fl-hr
          class="Divider"
          [color]="HrColor.XDARK"
        ></fl-hr>

        <div class="Footer">
          @if (prize !== undefined && prize > 0 && currencyCode !== undefined) {
            <p class="Prize text-body-20 font-bold">
              {{ prize | flCurrency: currencyCode : { withPrecision: false } }}
            </p>
          }
          @if (partnerLabel; as partner) {
            <p
              class="Partner text-body-12"
              i18n="Challenge card partnered enterprises"
              [attr.tabindex]="partnerTooltip !== '' ? 0 : null"
              [matTooltip]="partnerTooltip"
            >
              Presented by
              <span class="Partner-name text-subtitle-16">{{ partner }}</span>
            </p>
          }
        </div>
      </div>
    </ng-template>
  `,
  styleUrls: ['./challenge-card-default.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChallengeCardDefaultComponent implements OnDestroy {
  private cdr = inject(ChangeDetectorRef);
  private locale = inject(LOCALE_ID);
  private ngZone = inject(NgZone);
  private platformId = inject(PLATFORM_ID);

  CardCoverImageSize = DEFAULT_CARD_COVER_IMAGE_SIZE;
  HrColor = HrColor;
  IconSize = IconSize;
  LinkUnderline = LinkUnderline;
  PictureLayout = PictureLayout;
  QueryParamsHandling = QueryParamsHandling;
  TagSize = TagSize;
  TagColor = TagColor;

  readonly backgroundStyles: PictureBackgroundImageStyles = {
    position: 'center',
    objectFit: PictureBgImageObjectFit.COVER,
  };

  readonly entryCountPluralMapping = {
    '=1': $localize`:Challenge card entry count|Number of entries submitted to a challenge:entry`,
    other: $localize`:Challenge card entry count|Number of entries submitted to a challenge:entries`,
  };

  // FIXME: T300116 Enable hydration.
  // Currently fails when an i18n block follows an empty @if with
  // TypeError: Cannot read properties of null (reading 'nextSibling').
  @HostBinding('attr.ngSkipHydration') ngSkipHydration = true;

  @Input({ required: true }) challengeId: number;
  @Input({ required: true }) challengeTitle: string;
  @Input({ required: true }) startDate: number;
  @Input({ required: true }) status: ChallengeStatusTagType;

  @Input() currencyCode?: string;
  @Input() description?: string;
  @Input() entryCount?: number;
  @Input() imageUrl?: string;
  @Input() link?: string;
  @Input() partnerNames: readonly string[] = [];
  @Input() prize?: number;
  @Input() skills: readonly string[] = [];
  @Input() trackingLabel = 'ChallengeCardDefaultClicked';

  private resizeObserver?: ResizeObserver;
  private skillsRowWidth?: number;

  @ViewChild('skillsRow')
  set skillsRow(skillsRow: ElementRef<HTMLElement> | undefined) {
    this.resizeObserver?.disconnect();

    if (skillsRow !== undefined && isPlatformBrowser(this.platformId)) {
      this.resizeObserver = new ResizeObserver(entries => {
        const rowWidth = entries.at(-1)?.contentRect.width;
        if (rowWidth !== undefined && rowWidth !== this.skillsRowWidth) {
          // ResizeObserver callbacks run outside the Angular zone
          this.ngZone.run(() => {
            this.skillsRowWidth = rowWidth;
            this.cdr.markForCheck();
          });
        }
      });
      this.resizeObserver.observe(skillsRow.nativeElement);
    }
  }

  get displayedSkills(): readonly string[] {
    return this.skills.slice(0, this.visibleSkillCount);
  }

  get hiddenSkillCount(): number {
    return this.skills.length - this.visibleSkillCount;
  }

  get showEntryCount(): boolean {
    return this.entryCount !== undefined && this.startDate <= Date.now();
  }

  get startDateHasPassed(): boolean {
    return this.startDate <= Date.now();
  }

  get hiddenSkillsTooltip(): string {
    return new Intl.ListFormat(this.locale).format(
      this.skills.slice(this.visibleSkillCount),
    );
  }

  get partnerTooltip(): string {
    return this.partnerNames.length > 1
      ? new Intl.ListFormat(this.locale).format(this.partnerNames)
      : '';
  }

  get partnerLabel(): string | undefined {
    const [firstPartnerName, ...otherPartnerNames] = this.partnerNames;

    if (firstPartnerName === undefined) {
      return undefined;
    }

    return otherPartnerNames.length > 0
      ? `${firstPartnerName} +${otherPartnerNames.length}`
      : firstPartnerName;
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  private get visibleSkillCount(): number {
    if (this.skillsRowWidth === undefined) {
      return this.skills.length;
    }

    const tagWidths = this.skills.map(
      skill => skill.length * AVERAGE_CHAR_WIDTH + TAG_CHROME_WIDTH,
    );

    let fittedTagCount = 0;
    let usedWidth = 0;
    for (const [index, tagWidth] of tagWidths.entries()) {
      const gap = index > 0 ? TAG_GAP : 0;
      const plusTagReserve =
        this.skills.length > index + 1 ? TAG_GAP + PLUS_TAG_WIDTH : 0;
      if (usedWidth + gap + tagWidth + plusTagReserve <= this.skillsRowWidth) {
        fittedTagCount = index + 1;
      }
      usedWidth += gap + tagWidth;
    }

    return Math.max(1, fittedTagCount);
  }
}
